import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import Logo from '../components/Logo';
import SectionHeader from '../components/SectionHeader';
import { ReferralInfoSection, buildStandaloneReferralFields } from '../components/ReferralDetailsSection';
import EmailAttachments from '../components/EmailAttachments';
import ReferralAgreementEmails from '../components/ReferralAgreementEmails';
import ReferralAgreementTeam from '../components/ReferralAgreementTeam';
import ReferralBrokerageChecklist from '../components/ReferralBrokerageChecklist';
import { formatReferralDisplayAddress } from '../utils/referralDisplayUtils';
import './ContractDetail.css';
import '../components/ContractInfo.css';
import { getContractApiUrl } from '../utils/apiUrls';
import ChatBox from '../components/ChatBox';

const BACKEND_URL = getContractApiUrl();

const ReferralAgreementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionStates, setSectionStates] = useState({
    details: true,
    checklist: false,
    documents: false,
    team: true,
    emails: false,
  });

  const toggleSection = (key) => setSectionStates((s) => ({ ...s, [key]: !s[key] }));

  useEffect(() => {
    if (isAuthenticated && id && user?.email) fetchReferral();
  }, [isAuthenticated, id, user?.email]);

  const fetchReferral = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${BACKEND_URL}/referral-agreements/${id}`, {
        params: { user_email: user?.email },
      });
      setReferral(res.data);
    } catch (err) {
      if (err.response?.status === 404) setError('Referral agreement not found.');
      else setError('Failed to load referral agreement.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <h1>Access Denied</h1>
          <p>Please log in to view this referral agreement.</p>
          <button type="button" className="login-button" onClick={() => navigate('/')}>Go to Login</button>
        </div>
      </div>
    );
  }

  if (!user?.email) return <LoadingSpinner />;

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button type="button" onClick={fetchReferral} className="retry-button">Retry</button>
            <button type="button" onClick={() => navigate('/')} className="back-button">← Back</button>
          </div>
        </div>
      </div>
    );
  }

  const displayAddress = formatReferralDisplayAddress(referral);
  const sideLabel = referral.referral_side === 'seller' ? 'Seller Referral' : 'Buyer Referral';
  const propertyFields = buildStandaloneReferralFields(referral);

  return (
    <div className="contract-detail-container">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <Logo size={72} variant="arrow" />
            <div className="header-title-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h1 className="property-address-title">{displayAddress}</h1>
                <span className="detail-type-badge detail-type-badge--buyer-agreement">{sideLabel}</span>
              </div>
              <div className="contract-parties-subtitle">
                {referral.receiving_agent_name && (
                  <span>Receiving: {referral.receiving_agent_name}</span>
                )}
                {referral.skyslope_sync_status && (
                  <span style={{ marginLeft: referral.receiving_agent_name ? 8 : 0 }}>
                    · SkySlope: {referral.skyslope_sync_status}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="header-right">
            <button type="button" className="back-button" onClick={() => navigate('/')}>← Back</button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <ChatBox
          key={id ? `referral:${id}` : 'home'}
          userEmail={user?.email}
          userName={user?.name || user?.nickname}
          getAccessTokenSilently={getAccessTokenSilently}
          getIdTokenClaims={getIdTokenClaims}
          entityHint={id ? `referral:${id}` : undefined}
        />
        <section className="team-section">
          <ReferralAgreementTeam
            referralAgreementId={referral.id}
            team={referral.team}
            userEmail={user?.email}
            isExpanded={sectionStates.team}
            onToggle={() => toggleSection('team')}
          />
        </section>

        <section className="contract-info-section">
          <div className="contract-info">
            <SectionHeader
              title="Referral Details"
              isExpanded={sectionStates.details}
              onToggle={() => toggleSection('details')}
            />
            {sectionStates.details && (
              <div className="contract-info-container">
                <div className="info-section">
                  <div className="info-grid">
                    {propertyFields.map(([label, value]) => (
                      value != null && value !== '' ? (
                        <div key={label} className="info-item">
                          <span className="info-label">{label}:</span>
                          <span className="info-value">{value}</span>
                        </div>
                      ) : null
                    ))}
                  </div>
                </div>
                <ReferralInfoSection referralDetails={referral} />
              </div>
            )}
          </div>
        </section>

        {referral.brokerage_checklist?.length > 0 && (
          <section>
            <ReferralBrokerageChecklist
              items={referral.brokerage_checklist}
              syncStatus={referral.skyslope_sync_status}
              isExpanded={sectionStates.checklist}
              onToggle={() => toggleSection('checklist')}
            />
          </section>
        )}

        <section className="attachments-section">
          <EmailAttachments
            referralAgreementId={referral.id}
            propertyAddress={referral.property_address}
            title="Referral Documents"
            isExpanded={sectionStates.documents}
            onToggle={() => toggleSection('documents')}
          />
        </section>

        <ReferralAgreementEmails
          referralAgreementId={referral.id}
          userEmail={user?.email}
          isExpanded={sectionStates.emails}
          onToggle={() => toggleSection('emails')}
        />
      </main>
    </div>
  );
};

export default ReferralAgreementDetail;
